import { ArrowRight } from 'lucide-react-native';
import { Image, Pressable, StyleSheet, View } from 'react-native';

import { Avatar } from '@/components/Avatar';
import { BrandLogo } from '@/components/BrandLogo';
import { Pill } from '@/components/Pill';
import { Text } from '@/components/Text';
import { colors, fontSizes, radii, spacing } from '@/constants/theme';
import { settledCard, socialStage } from '@/lib/socialProgress';
import type { Action, Deal } from '@/types/deal';

export type SocialDealCardProps = {
  deal: Deal;
  action?: Action;
  onPress: () => void;
  testID?: string;
};

/**
 * One social deal on the Create tab (Figma "Create" frame): cover photo (when
 * the deal has one) with the server's badge, the brand and the deal, and a footer naming the user's
 * next move. Not started: the first step's own button label ("Apply"). Under
 * way: "Keep going" and how far along. Over: how it ended, the server's card
 * title ("Approved", "This deal expired").
 */
export function SocialDealCard({ deal, action, onPress, testID }: SocialDealCardProps) {
  const badge = deal.marketing_copy?.social?.badge;
  const firstCta = deal.steps_config.steps[0]?.ctas?.find((c) => c.kind === 'primary')?.label;
  const stage = socialStage(action);
  const ended = settledCard(action)?.title;
  const footer = stage === 'under_way' ? 'Keep going' : stage === 'new' ? (firstCta ?? 'View deal') : (ended ?? 'View deal');
  const progress = action?.voltr?.progress_label ?? undefined;

  const pill = badge ? <Pill tone="green" testID={testID ? `${testID}-badge` : undefined}>{badge}</Pill> : null;

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={[deal.brand_name, deal.title, footer, progress].filter(Boolean).join(', ')}
      style={({ pressed }) => [styles.card, pressed && styles.pressed]}
      testID={testID}
    >
      {deal.cover_image_url ? (
        <View style={styles.cover}>
          <Image source={{ uri: deal.cover_image_url }} style={styles.image} resizeMode="cover" />
          {pill ? <View style={styles.onCover}>{pill}</View> : null}
        </View>
      ) : pill ? (
        <View style={styles.noCover}>{pill}</View>
      ) : null}

      <View style={styles.brandRow}>
        <BrandLogo
          url={deal.brand_logo_url}
          size={LOGO}
          radius={radii.sm}
          fallback={<Avatar initials={deal.brand_name.slice(0, 2).toUpperCase()} size={LOGO} tone="electric" />}
        />
        <View style={styles.titles}>
          <Text variant="caption" color="textMuted" uppercase numberOfLines={1} style={styles.eyebrow}>
            {deal.brand_name}
          </Text>
          <Text variant="bodyBold" color="deepGreen" numberOfLines={2} style={styles.title}>
            {deal.title}
          </Text>
        </View>
      </View>

      <View style={styles.footer}>
        <View style={styles.footerText}>
          <Text variant="bodyBold" color="deepGreen">
            {footer}
          </Text>
          {progress ? (
            <Text
              variant="caption"
              color="textMuted"
              numberOfLines={1}
              style={styles.progress}
              testID={testID ? `${testID}-progress` : undefined}
            >
              {progress}
            </Text>
          ) : null}
        </View>
        <View style={styles.arrow}>
          <ArrowRight size={14} color={colors.frost} strokeWidth={2.5} />
        </View>
      </View>
    </Pressable>
  );
}

const LOGO = 40;
const ARROW = 24;

const styles = StyleSheet.create({
  card: {
    backgroundColor: colors.surface,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.md,
    gap: spacing.md,
  },
  pressed: {
    opacity: 0.7,
  },
  // A deal with no photo has no cover block; its badge then leads the card.
  cover: {
    aspectRatio: 16 / 9,
    borderRadius: radii.base,
    backgroundColor: colors.surfaceSunken,
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  onCover: {
    position: 'absolute',
    top: spacing.sm,
    left: spacing.sm,
  },
  noCover: {
    alignItems: 'flex-start',
  },
  brandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  titles: {
    flex: 1,
    gap: 2,
  },
  eyebrow: {
    fontSize: fontSizes.xs,
    letterSpacing: 0.4,
  },
  title: {
    fontSize: fontSizes.lg,
    lineHeight: 24,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  // A long server label shortens rather than wrapping the row.
  progress: {
    flexShrink: 1,
  },
  footerText: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: spacing.sm,
  },
  arrow: {
    width: ARROW,
    height: ARROW,
    borderRadius: radii.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.deepGreen,
  },
});
